import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuthenticatedQueryFn } from '@/hooks/useAuthenticatedQuery';
import { shouldSuppressGlobalErrorToast } from '@/lib/queryErrorToast';
import { ToastVariant } from '@/components/ui/toast';
import { useToast } from '@/components/ui/use-toast';
import { deleteLink } from '@/services/link.service';
import { error as errorLog } from '@/utilities/log';
import { QueryKey } from '@/lib/queryKeys';
import { useTranslations } from 'next-intl';

export const useDeleteLinkMutation = () => {
  const tErrors = useTranslations('errors');
  const tCommon = useTranslations('common');
  const tToasts = useTranslations('toasts');

  const { toast } = useToast();
  const queryClient = useQueryClient();
  const deleteLinkWithAuth = useAuthenticatedQueryFn(deleteLink);

  const deleteLinkMutation = useMutation({
    mutationFn: async ({ nodeId, linkId }: { nodeId: string; linkId: string }) => {
      await deleteLinkWithAuth({ nodeId, linkId });
    },
    onSuccess: (_data, { nodeId }) => {
      toast({
        title: tToasts('deleteLinkMutation.title'),
        description: tToasts('deleteLinkMutation.description'),
      });

      void queryClient.invalidateQueries({ queryKey: [QueryKey.NodeLinkList, nodeId] });
    },
    onError: (err) => {
      if (shouldSuppressGlobalErrorToast(err)) {
        return;
      }

      errorLog('Action failed:', err);

      toast({
        variant: ToastVariant.DESTRUCTIVE,
        title: tCommon('error'),
        description: err.message ?? tErrors('toastDefaultDescription'),
      });
    },
  });

  return { deleteLinkMutation };
};
